import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatCurrency, formatDate } from "@/lib/demo-data";
import { ListPageSkeleton } from "@/components/PageSkeleton";
import { DollarSign, CheckCircle2, Send, FileText, AlertCircle, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";

type InvoiceStatusFilter = "ALL" | "DRAFT" | "SENT" | "PAID" | "CANCELLED";
type AssistanceStatusFilter = "ALL" | "DUE" | "PAID" | "WAIVED";
type SuccessFeeStatusFilter = "ALL" | "DUE" | "PAID";

export default function Billing() {
  const { user } = useAuth();
  const canAct = canWrite(user?.staffRole);
  const isSuperAdmin = user?.staffRole === "SUPER_ADMIN";
  const [assistancePage, setAssistancePage] = useState(1);
  const [successFeePage, setSuccessFeePage] = useState(1);
  const [serviceInvoicePage, setServiceInvoicePage] = useState(1);
  const [invoiceFilter, setInvoiceFilter] = useState<InvoiceStatusFilter>("ALL");
  const [assistanceFilter, setAssistanceFilter] = useState<AssistanceStatusFilter>("ALL");
  const [successFeeFilter, setSuccessFeeStatusFilter] = useState<SuccessFeeStatusFilter>("ALL");

  const { data: stats, isLoading: statsLoading, isError: statsError, refetch: refetchStats } = trpc.admin.billing.stats.useQuery();

  const { data: assistanceData, isLoading: assistanceLoading, isError: assistanceError, refetch: refetchAssistance } =
    trpc.admin.billing.assistanceOrders.useQuery({
      page: assistancePage,
      limit: 20,
      status: assistanceFilter === "ALL" ? undefined : assistanceFilter,
    });

  const { data: successFeeData, isLoading: successFeeLoading, isError: successFeeError, refetch: refetchSuccessFee } =
    trpc.admin.billing.successFees.useQuery({
      page: successFeePage,
      limit: 20,
      status: successFeeFilter === "ALL" ? undefined : successFeeFilter,
    });

  const { data: serviceInvoiceData, isLoading: serviceInvoiceLoading, isError: serviceInvoiceError, refetch: refetchServiceInvoice } =
    trpc.admin.billing.serviceInvoices.useQuery({
      page: serviceInvoicePage,
      limit: 20,
      status: invoiceFilter === "ALL" ? undefined : invoiceFilter,
    });

  const markAssistancePaidMutation = trpc.admin.billing.markAssistancePaid.useMutation({
    onSuccess: () => {
      toast.success("Commande marquée comme payée");
      refetchAssistance();
      refetchStats();
    },
    onError: (err) => toast.error(err.message),
  });

  const markSuccessFeePaidMutation = trpc.admin.billing.markSuccessFeePaid.useMutation({
    onSuccess: () => {
      toast.success("Success fee marqué comme payé");
      refetchSuccessFee();
      refetchStats();
    },
    onError: (err) => toast.error(err.message),
  });

  const markServiceInvoicePaidMutation = trpc.admin.billing.markServiceInvoicePaid.useMutation({
    onSuccess: () => {
      toast.success("Facture marquée comme payée");
      refetchServiceInvoice();
      refetchStats();
    },
    onError: (err) => toast.error(err.message),
  });

  const sendServiceInvoiceMutation = trpc.admin.billing.sendServiceInvoice.useMutation({
    onSuccess: () => {
      toast.success("Facture envoyée");
      refetchServiceInvoice();
      refetchStats();
    },
    onError: (err) => toast.error(err.message),
  });

  const exportRevenueMutation = trpc.admin.billing.exportRevenue.useMutation({
    onSuccess: (data: { filename: string; csv: string; count: number }) => {
      const blob = new Blob([data.csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Export téléchargé — ${data.count} lignes`);
    },
    onError: () => toast.error("Erreur lors de l'export"),
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-semibold">Facturation</h1>
            <p className="text-muted-foreground">Suivi des revenus, factures service et success fees</p>
          </div>
          {isSuperAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportRevenueMutation.mutate({})}
              disabled={exportRevenueMutation.isPending}
            >
              <Download className="h-4 w-4 mr-2" />
              {exportRevenueMutation.isPending ? "Export…" : "Export Revenus CSV"}
            </Button>
          )}
        </div>

        {/* KPIs — 2 rows */}
        {statsLoading ? (
          <ListPageSkeleton rows={2} />
        ) : statsError ? (
          <div className="flex flex-col items-center justify-center min-h-[200px] gap-4">
            <AlertCircle className="h-12 w-12 text-destructive/40" />
            <p className="text-muted-foreground font-medium">Erreur de chargement des statistiques</p>
            <Button variant="outline" onClick={() => refetchStats()}>
              <RefreshCw className="h-4 w-4 mr-2" />Réessayer
            </Button>
          </div>
        ) : stats ? (
          <>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">CA Service (mois)</CardTitle>
                  <DollarSign className="h-4 w-4 text-success" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-success">
                    {formatCurrency(stats.serviceRevenue || 0)}
                  </div>
                  <p className="text-xs text-muted-foreground">Factures payées ce mois</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Factures à émettre</CardTitle>
                  <FileText className="h-4 w-4 text-warning" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-warning">{stats.invoicesToIssue || 0}</div>
                  <p className="text-xs text-muted-foreground">Brouillons à envoyer</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">En attente paiement</CardTitle>
                  <AlertCircle className="h-4 w-4 text-warning" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.invoicesPending || 0}</div>
                  <p className="text-xs text-muted-foreground">Factures envoyées</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Payées ce mois</CardTitle>
                  <CheckCircle2 className="h-4 w-4 text-success" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.invoicesPaidThisMonth || 0}</div>
                  <p className="text-xs text-muted-foreground">Factures réglées</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Assistance à facturer</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.assistanceDueCount || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    {formatCurrency(stats.assistanceDueTotal || 0)} total
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Assistance encaissée</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-success">
                    {formatCurrency(stats.assistancePaidTotal || 0)}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Success fees à facturer</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.successFeeDueCount || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    {formatCurrency(stats.successFeeDueTotal || 0)} total
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Success fees encaissés</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-success">
                    {formatCurrency(stats.successFeePaidTotal || 0)}
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        ) : null}

        <Tabs defaultValue="service-invoices" className="space-y-4">
          <TabsList>
            <TabsTrigger value="service-invoices">
              Factures Service{serviceInvoiceData?.total != null ? ` (${serviceInvoiceData.total})` : ""}
            </TabsTrigger>
            <TabsTrigger value="assistance">
              Assistance{assistanceData?.total != null ? ` (${assistanceData.total})` : ""}
            </TabsTrigger>
            <TabsTrigger value="success-fees">
              Success Fees{successFeeData?.total != null ? ` (${successFeeData.total})` : ""}
            </TabsTrigger>
          </TabsList>

          {/* Service Invoices */}
          <TabsContent value="service-invoices">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Factures Service</CardTitle>
                  <Select value={invoiceFilter} onValueChange={(v) => { setInvoiceFilter(v as InvoiceStatusFilter); setServiceInvoicePage(1); }}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">Tous statuts</SelectItem>
                      <SelectItem value="DRAFT">Brouillon</SelectItem>
                      <SelectItem value="SENT">Envoyée</SelectItem>
                      <SelectItem value="PAID">Payée</SelectItem>
                      <SelectItem value="CANCELLED">Annulée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {serviceInvoiceLoading ? (
                  <ListPageSkeleton />
                ) : serviceInvoiceError ? (
                  <div className="flex flex-col items-center justify-center min-h-[200px] gap-4">
                    <AlertCircle className="h-12 w-12 text-destructive/40" />
                    <p className="text-muted-foreground font-medium">Erreur de chargement des factures</p>
                    <Button variant="outline" onClick={() => refetchServiceInvoice()}>
                      <RefreshCw className="h-4 w-4 mr-2" />Réessayer
                    </Button>
                  </div>
                ) : serviceInvoiceData && serviceInvoiceData.items.length > 0 ? (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Entreprise</TableHead>
                          <TableHead>Projet / AO</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Montant</TableHead>
                          <TableHead>Statut</TableHead>
                          <TableHead>Émise le</TableHead>
                          <TableHead>Échéance</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {serviceInvoiceData.items.map((invoice: any) => (
                          <TableRow key={invoice.id}>
                            <TableCell className="font-medium">{invoice.Company?.name || "-"}</TableCell>
                            <TableCell className="max-w-xs truncate">
                              {invoice.ResponseProject?.Tender?.title || "-"}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{invoice.type}</Badge>
                            </TableCell>
                            <TableCell className="font-medium">{formatCurrency(invoice.amount)}</TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  invoice.status === "PAID"
                                    ? "default"
                                    : invoice.status === "SENT"
                                      ? "secondary"
                                      : invoice.status === "CANCELLED"
                                        ? "destructive"
                                        : "outline"
                                }
                              >
                                {invoice.status === "DRAFT" && "Brouillon"}
                                {invoice.status === "SENT" && "Envoyée"}
                                {invoice.status === "PAID" && "Payée"}
                                {invoice.status === "CANCELLED" && "Annulée"}
                              </Badge>
                            </TableCell>
                            <TableCell>{invoice.issuedAt ? formatDate(invoice.issuedAt) : "-"}</TableCell>
                            <TableCell>{invoice.dueAt ? formatDate(invoice.dueAt) : "-"}</TableCell>
                            <TableCell>
                              {canAct ? (
                                <div className="flex gap-1">
                                  {invoice.status === "DRAFT" && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => sendServiceInvoiceMutation.mutate({ id: invoice.id })}
                                      disabled={sendServiceInvoiceMutation.isPending}
                                    >
                                      <Send className="mr-1 h-3 w-3" />
                                      Envoyer
                                    </Button>
                                  )}
                                  {(invoice.status === "SENT" || invoice.status === "DRAFT") && (
                                    <Button
                                      size="sm"
                                      onClick={() => markServiceInvoicePaidMutation.mutate({ id: invoice.id })}
                                      disabled={markServiceInvoicePaidMutation.isPending}
                                    >
                                      <CheckCircle2 className="mr-1 h-3 w-3" />
                                      Payé
                                    </Button>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {serviceInvoiceData.total > serviceInvoiceData.limit && (
                      <div className="flex justify-between items-center mt-4">
                        <span className="text-sm text-muted-foreground">
                          Page {serviceInvoiceData.page} / {Math.ceil(serviceInvoiceData.total / serviceInvoiceData.limit)}
                        </span>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" disabled={serviceInvoicePage <= 1} onClick={() => setServiceInvoicePage(p => p - 1)}>Précédent</Button>
                          <Button size="sm" variant="outline" disabled={serviceInvoicePage >= Math.ceil(serviceInvoiceData.total / serviceInvoiceData.limit)} onClick={() => setServiceInvoicePage(p => p + 1)}>Suivant</Button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-muted-foreground">Aucune facture service</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Assistance */}
          <TabsContent value="assistance">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Commandes d'assistance</CardTitle>
                  <Select value={assistanceFilter} onValueChange={(v) => { setAssistanceFilter(v as AssistanceStatusFilter); setAssistancePage(1); }}>
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">Tous statuts</SelectItem>
                      <SelectItem value="DUE">Dû</SelectItem>
                      <SelectItem value="PAID">Payé</SelectItem>
                      <SelectItem value="WAIVED">Annulé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {assistanceLoading ? (
                  <ListPageSkeleton />
                ) : assistanceError ? (
                  <div className="flex flex-col items-center justify-center min-h-[200px] gap-4">
                    <AlertCircle className="h-12 w-12 text-destructive/40" />
                    <p className="text-muted-foreground font-medium">Erreur de chargement des commandes</p>
                    <Button variant="outline" onClick={() => refetchAssistance()}>
                      <RefreshCw className="h-4 w-4 mr-2" />Réessayer
                    </Button>
                  </div>
                ) : assistanceData && assistanceData.items.length > 0 ? (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Entreprise</TableHead>
                          <TableHead>Projet / AO</TableHead>
                          <TableHead>Montant</TableHead>
                          <TableHead>Statut</TableHead>
                          <TableHead>Créé le</TableHead>
                          <TableHead>Payé le</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {assistanceData.items.map((order: any) => (
                          <TableRow key={order.id}>
                            <TableCell className="font-medium">{order.Company?.name || "-"}</TableCell>
                            <TableCell className="max-w-xs truncate">
                              {order.ResponseProject?.Tender?.title || "-"}
                            </TableCell>
                            <TableCell className="font-medium">{formatCurrency(order.priceFixed / 100)}</TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  order.status === "PAID"
                                    ? "default"
                                    : order.status === "WAIVED"
                                      ? "secondary"
                                      : "outline"
                                }
                              >
                                {order.status === "DUE" && "Dû"}
                                {order.status === "PAID" && "Payé"}
                                {order.status === "WAIVED" && "Annulé"}
                              </Badge>
                            </TableCell>
                            <TableCell>{formatDate(order.createdAt)}</TableCell>
                            <TableCell>{order.paidAt ? formatDate(order.paidAt) : "-"}</TableCell>
                            <TableCell>
                              {order.status === "DUE" && canAct && (
                                <Button
                                  size="sm"
                                  onClick={() => markAssistancePaidMutation.mutate({ id: order.id })}
                                  disabled={markAssistancePaidMutation.isPending}
                                >
                                  <CheckCircle2 className="mr-1 h-3 w-3" />
                                  Payé
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {assistanceData.total > assistanceData.limit && (
                      <div className="flex justify-between items-center mt-4">
                        <span className="text-sm text-muted-foreground">
                          Page {assistanceData.page} / {Math.ceil(assistanceData.total / assistanceData.limit)}
                        </span>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" disabled={assistancePage <= 1} onClick={() => setAssistancePage(p => p - 1)}>Précédent</Button>
                          <Button size="sm" variant="outline" disabled={assistancePage >= Math.ceil(assistanceData.total / assistanceData.limit)} onClick={() => setAssistancePage(p => p + 1)}>Suivant</Button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12">
                    <DollarSign className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-muted-foreground">Aucune commande d'assistance</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Success Fees */}
          <TabsContent value="success-fees">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Success Fees</CardTitle>
                  <Select value={successFeeFilter} onValueChange={(v) => { setSuccessFeeStatusFilter(v as SuccessFeeStatusFilter); setSuccessFeePage(1); }}>
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">Tous statuts</SelectItem>
                      <SelectItem value="DUE">Dû</SelectItem>
                      <SelectItem value="PAID">Payé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {successFeeLoading ? (
                  <ListPageSkeleton />
                ) : successFeeError ? (
                  <div className="flex flex-col items-center justify-center min-h-[200px] gap-4">
                    <AlertCircle className="h-12 w-12 text-destructive/40" />
                    <p className="text-muted-foreground font-medium">Erreur de chargement des success fees</p>
                    <Button variant="outline" onClick={() => refetchSuccessFee()}>
                      <RefreshCw className="h-4 w-4 mr-2" />Réessayer
                    </Button>
                  </div>
                ) : successFeeData && successFeeData.items.length > 0 ? (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Entreprise</TableHead>
                          <TableHead>Projet / AO</TableHead>
                          <TableHead>Montant marché</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Commission</TableHead>
                          <TableHead>Statut</TableHead>
                          <TableHead>Émis le</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {successFeeData.items.map((invoice: any) => (
                          <TableRow key={invoice.id}>
                            <TableCell className="font-medium">{invoice.Company?.name || "-"}</TableCell>
                            <TableCell className="max-w-xs truncate">
                              {invoice.ResponseProject?.Tender?.title || "-"}
                            </TableCell>
                            <TableCell>{formatCurrency(invoice.baseAmount)}</TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {invoice.feeType === "PERCENT" ? `${invoice.percent}%` : "Fixe"}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium">{formatCurrency(invoice.feeAmount)}</TableCell>
                            <TableCell>
                              <Badge variant={invoice.status === "PAID" ? "default" : "outline"}>
                                {invoice.status === "DUE" && "Dû"}
                                {invoice.status === "PAID" && "Payé"}
                              </Badge>
                            </TableCell>
                            <TableCell>{formatDate(invoice.issuedAt)}</TableCell>
                            <TableCell>
                              {invoice.status === "DUE" && canAct && (
                                <Button
                                  size="sm"
                                  onClick={() => markSuccessFeePaidMutation.mutate({ id: invoice.id })}
                                  disabled={markSuccessFeePaidMutation.isPending}
                                >
                                  <CheckCircle2 className="mr-1 h-3 w-3" />
                                  Payé
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {successFeeData.total > successFeeData.limit && (
                      <div className="flex justify-between items-center mt-4">
                        <span className="text-sm text-muted-foreground">
                          Page {successFeeData.page} / {Math.ceil(successFeeData.total / successFeeData.limit)}
                        </span>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" disabled={successFeePage <= 1} onClick={() => setSuccessFeePage(p => p - 1)}>Précédent</Button>
                          <Button size="sm" variant="outline" disabled={successFeePage >= Math.ceil(successFeeData.total / successFeeData.limit)} onClick={() => setSuccessFeePage(p => p + 1)}>Suivant</Button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle2 className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-muted-foreground">Aucun success fee</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
